// One shared recovery for pages left open across a new deploy: when a piece of the site's own code from the
// old deploy is gone, reload once to fetch the new version. Startup handlers (main.tsx) and ErrorBoundary use
// the same guard, so together they reload at most once every 10 seconds and never loop.

const KEY = "eal-chunk-reload-at";
const WINDOW_MS = 10_000;

/**
 * True only for failures to load the site's own code files. Ordinary request failures
 * ("Failed to fetch", "NetworkError") are not stale deploys and are left alone.
 */
export function isStaleChunkError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null | undefined;
  const text = typeof error === "string" ? error : `${e?.name ?? ""} ${e?.message ?? String(error ?? "")}`;
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk \S+ failed|ChunkLoadError/i.test(text);
}

/**
 * Reloads the page if no automatic reload happened in the last 10 seconds. Returns true only when it
 * reloads; false means the caller must let the original error surface. Without session storage the
 * guard can't be kept, so it never reloads automatically.
 */
export function tryStaleChunkReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(KEY) || 0);
    if (Date.now() - last <= WINDOW_MS) return false;
    window.sessionStorage.setItem(KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** A person pressing "Reload page" starts a fresh allowance; each press is one reload, so it can't loop. */
export function clearStaleChunkReloadGuard() {
  try { window.sessionStorage.removeItem(KEY); } catch { /* storage unavailable */ }
}
